import { z } from "zod";

import { verificationCodeSchema } from "./verifications";

const dbTimeRegex = /^\d{2}:\d{2}$/;
export const dbTimeSchema = z
  .string()
  .refine((value) => dbTimeRegex.test(value), {
    message: "time must be in HH:MM format",
  });

export const checkoutPaymentTypeSchema = z.enum(["cash", "credit-card"]);
export const checkoutCaptureTypeSchema = z.enum([
  "manual",
  "auto",
  "tap_to_pay",
]);

export const ManualCheckoutSchema = z.object({
  appointmentId: z.string().min(1, "appointmentId is required"),
  price: z
    .number({ message: "price must be a number" })
    .min(0, "price must be at least 0"),
  paymentType: checkoutPaymentTypeSchema,
  tip: z
    .number({ message: "tip must be a number" })
    .min(0, "tip must be at least 0")
    .optional(),
  tipPaymentType: checkoutPaymentTypeSchema.optional(),
  note: z
    .string({ message: "note must be a string" })
    .max(1000, "note is too long")
    .optional(),
  type: checkoutCaptureTypeSchema.optional(),
  stripeChargeId: z.string().optional(),
  stripeReceiptUrl: z.string().url().optional(),
  receiptEmail: z.string().email().optional(),
  /**
   * Optional coupon code. When present, `price` is treated as the GROSS
   * (pre-discount) service total and the server computes the discount and
   * the amount actually charged. A client-supplied discount amount is never
   * accepted.
   */
  offerCode: z.string().min(1).max(32).optional(),
});

export const ServiceItemSchema = z.object({
  id: z.string().min(1, "serviceId is required"),
  optionId: z.string().optional(),
});

/**
 * Server-injected, signed Google Ads Autopilot landing session. The browser
 * never reads this value: the same-origin appointments proxy removes any
 * caller-supplied field and replaces it with the HttpOnly cookie value.
 */
export const googleAdsAutopilotAttributionSessionSchema = z
  .string()
  .min(1)
  .max(2048)
  .regex(/^[A-Za-z0-9._-]+$/);

export const googleAdsAutopilotAttributionDispositionSchema = z.enum([
  "CONSUMED",
  "RETAIN",
  "UNAVAILABLE",
]);
export type GoogleAdsAutopilotAttributionDisposition = z.infer<
  typeof googleAdsAutopilotAttributionDispositionSchema
>;

/**
 * Opaque caller-provided metadata for audit correlation
 * (e.g. OSBP mandate id, idempotency key, source/agent identity).
 *
 * Constraints:
 * - String → string only (no nested objects, no arrays, no numbers/bools).
 * - Max 16 keys, key length ≤ 64, value length ≤ 500.
 * - Total serialized size capped at 4 KB.
 *
 * Access control (enforced by routes, not the schema):
 * - Writable only on authorized appointment mutations.
 * - Readable only on authorized appointment reads (e.g. GET
 *   /v1/appointments/:id requires owner/member auth). NEVER returned on
 *   public surfaces such as the review page or phone-keyed history.
 *
 * Callers must NOT place PII, verification codes, or secrets here.
 */
export const appointmentMetadataSchema = z
  .record(
    z
      .string()
      .min(1, "metadata key cannot be empty")
      .max(64, "metadata key too long"),
    z.string().max(500, "metadata value too long"),
  )
  .refine((value) => Object.keys(value).length <= 16, {
    message: "metadata cannot have more than 16 keys",
  })
  .refine((value) => JSON.stringify(value).length <= 4096, {
    message: "metadata exceeds 4KB",
  });

export const CreateAppointmentBaseSchema = z.object({
  salonId: z.string(),
  userId: z.string(),
  scheduleId: z.string(),
  customerId: z.string(),
  date: z.string(),
  time: dbTimeSchema,
  services: z
    .array(ServiceItemSchema)
    .nonempty("at least one service required"),
  verificationCode: verificationCodeSchema.optional(),
  paymentMethodId: z.string().optional(),
  googleAdsAutopilotAttributionSession:
    googleAdsAutopilotAttributionSessionSchema.optional(),
  /**
   * The Find profile handle this booking came from (plans/find-listing-spec.md).
   * Server-injected like the Google Ads session: the same-origin appointments
   * proxy removes any caller-supplied value and sets it from its HttpOnly
   * cookie, and the API honors it only from the trusted proxy.
   */
  findListingHandle: z.string().min(3).max(30).optional(),
  /**
   * **Operator-only.** Books the appointment even when the slot is not on the
   * openings grid (overlaps, past times, off-hours). Requires an
   * authenticated session belonging to the salon's owner or an active team
   * member: unauthenticated use returns `401 UNAUTHENTICATED`, an
   * authenticated non-member returns `403 UNAUTHORIZED`.
   */
  allowUnavailableSlot: z
    .boolean()
    .optional()
    .describe(
      "Operator-only: books off-grid slots (overlaps, past times, " +
        "off-hours). Requires an authenticated owner/team-member session — " +
        "401 UNAUTHENTICATED / 403 UNAUTHORIZED otherwise.",
    ),
  /**
   * **Operator-only.** Skips customer verification, velocity limits, the
   * customer block/deposit gate, and payment collection — the dashboard's
   * manual-booking path. Same authentication requirement as
   * `allowUnavailableSlot`: `401`/`403` without an authorized operator
   * session.
   */
  skipPaymentCollection: z
    .boolean()
    .optional()
    .describe(
      "Operator-only: skips customer verification, velocity limits, the " +
        "customer gate, and payment collection (dashboard manual booking). " +
        "Requires an authenticated owner/team-member session — 401/403 " +
        "otherwise.",
    ),
  /**
   * **Operator-only.** `false` skips the customer's confirmation and the
   * business's and assigned member's new-booking notices. Honored only on a
   * request that passed the operator override gate (an authorized owner or
   * team member sending one of the operator-only fields); ignored otherwise,
   * so a public booking always runs the normal notifications. Omitted means
   * notify.
   */
  sendNotifications: z.boolean().optional(),
  /**
   * **Operator-only.** Optional name or note for a walk-in ("Mike"), shown
   * as "Walk-in · Mike". Accepted only when `customerId` is the business's
   * walk-in customer (`POST /v1/customers/walk-in`); `422` otherwise. Same
   * authentication requirement as `allowUnavailableSlot`.
   */
  guestLabel: z
    .string()
    .trim()
    .max(60, "guestLabel cannot exceed 60 characters")
    .optional()
    .describe(
      "Operator-only: a walk-in's optional name or note, accepted only " +
        "with the business's walk-in customer. Requires an authenticated " +
        "owner/team-member session — 401/403 otherwise.",
    ),
  /**
   * Optional opaque metadata for audit correlation. See
   * `appointmentMetadataSchema` for shape and access rules.
   */
  metadata: appointmentMetadataSchema.optional(),
  /**
   * **Operator-only.** Optional override for the appointment's total
   * duration in minutes. Requires an authenticated owner/team-member session
   * (`401`/`403` otherwise, like `allowUnavailableSlot`); intended for
   * dashboard/staff-initiated bookings and ignored for quoted-request
   * overrides. Service snapshots retain their catalog durations — this only
   * affects the appointment's end time and occupied slots.
   */
  customDurationMinutes: z
    .number({ message: "customDurationMinutes must be a number" })
    .int("customDurationMinutes must be an integer")
    .min(1, "customDurationMinutes must be at least 1")
    .max(24 * 60, "customDurationMinutes cannot exceed 24 hours")
    .optional()
    .describe(
      "Operator-only: overrides the appointment's total duration in " +
        "minutes (end time and occupied slots; service snapshots keep " +
        "catalog durations). Requires an authenticated owner/team-member " +
        "session — 401/403 otherwise.",
    ),
});

export const CreateAppointmentWithoutQuoteSchema =
  CreateAppointmentBaseSchema.extend({
    requestId: z.undefined().optional(),
    quotePublicId: z.undefined().optional(),
  });

export const CreateAppointmentWithQuoteSchema =
  CreateAppointmentBaseSchema.extend({
    requestId: z.string().min(1),
    /**
     * Version/capability token for a quoted service request. It is paired with
     * `requestId` so a page opened from an older quote link cannot consume newly
     * reissued quote terms.
     */
    quotePublicId: z.string().min(1),
  });

/** A booking either identifies an exact quote revision or is not quote-backed. */
export const CreateAppointmentSchema = z.union([
  CreateAppointmentWithoutQuoteSchema,
  CreateAppointmentWithQuoteSchema,
]);

export const RescheduleAppointmentSchema = z.object({
  rescheduleAppointmentId: z
    .string()
    .min(1, "rescheduleAppointmentId is required"),
  userId: z.string().min(1, "userId is required"),
  services: z
    .array(ServiceItemSchema)
    .nonempty("at least one service required"),
  scheduleId: z.string(),
  date: z.string(),
  time: dbTimeSchema,
  verificationCode: verificationCodeSchema.optional(),
  phoneNumber: z.string().optional(),
  email: z.string().email().optional(),
  paymentMethodId: z.string().optional(),
  // Explicitly reject cached quote-client markers instead of letting Zod's
  // default unknown-key stripping turn a quote reschedule into a catalog one.
  requestId: z.undefined().optional(),
  quotePublicId: z.undefined().optional(),
  /**
   * **Operator-only.** Reschedules into a slot that is not on the openings
   * grid. Requires an authenticated owner/team-member session; the public
   * OTP-verified customer path may NOT use it (`401 UNAUTHENTICATED`,
   * checked before any verification code is consumed).
   */
  allowUnavailableSlot: z
    .boolean()
    .optional()
    .describe(
      "Operator-only: reschedules into off-grid slots. Requires an " +
        "authenticated owner/team-member session; the OTP-verified public " +
        "path may not use it (401, checked before the verification code " +
        "is consumed).",
    ),
  /**
   * Accepted for wire compatibility and **ignored**. Booking-source
   * attribution is immutable for an appointment's lineage (booking standard
   * § 14, `BF-DEC-005`), so the replacement keeps the original appointment's
   * metadata and a reschedule caller cannot assert origin fields (#689).
   */
  metadata: appointmentMetadataSchema.optional(),
});

export const appointmentCancellationReasonSchema = z.enum([
  "UNSPECIFIED",
  "SICK",
  "SCHEDULE_CONFLICT",
  "MOVED",
  "OTHER",
]);

export const CancelAppointmentSchema = z.object({
  appointmentId: z.string().min(1, "appointmentId is required"),
  code: verificationCodeSchema,
  reasonCode: appointmentCancellationReasonSchema.optional(),
  reasonNote: z
    .string({ message: "reasonNote must be a string" })
    .max(500, "reason note is too long")
    .optional(),
});

/**
 * What happened to a charged booking deposit when the appointment was
 * canceled (Sep 2026). KEPT: retained under the cancellation policy or the
 * operator's choice. REFUND_STARTED: refunded or refunding to the card.
 * NEEDS_ATTENTION: the automatic refund did not complete; the business was
 * told and can retry. NONE: no charged deposit.
 */
export const cancelledDepositOutcomeSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("NONE") }),
  z.object({ outcome: z.literal("KEPT"), amountMinor: z.number().int() }),
  z.object({ outcome: z.literal("REFUND_STARTED"), amountMinor: z.number().int() }),
  z.object({ outcome: z.literal("NEEDS_ATTENTION"), amountMinor: z.number().int() }),
]);
export type CancelledDepositOutcome = z.infer<typeof cancelledDepositOutcomeSchema>;

export const appointmentOperatorStatusRequestSchema = z.object({
  status: z.enum(["CANCELED", "NO_SHOW"]),
  reasonCode: appointmentCancellationReasonSchema.optional(),
  reasonNote: z.string().trim().max(500).optional(),
  /**
   * CANCELED only: refund the charged deposit (true) or keep it (false).
   * Requires refund permission when true. Omitted keeps it, as every client
   * did before Sep 2026.
   */
  refundDeposit: z.boolean().optional(),
});

export const appointmentOperatorStatusResponseSchema = z.object({
  success: z.boolean(),
  salonId: z.string().min(1),
  /** Absent from older API payloads and same-state retries of no-shows. */
  deposit: cancelledDepositOutcomeSchema.optional(),
});

export const appointmentOperatorRescheduleRequestSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: dbTimeSchema,
  scheduleId: z.string().min(1).optional(),
});

export const appointmentOperatorRescheduleResponseSchema = z.object({
  appointmentId: z.string().min(1),
  salonId: z.string().min(1),
});

export const AddNoteSchema = z.object({
  appointmentId: z.string().min(1, "appointmentId is required"),
  note: z.string().min(1, "note is required"),
});

/**
 * "Link customer": move a walk-in visit to a real customer of the same
 * business (plans/walk-in-customer-spec.md § 3). Either an existing customer,
 * or a new one found or created by phone as `POST /v1/customers` does.
 */
export const linkAppointmentCustomerRequestSchema = z.union([
  z.object({ customerId: z.string().min(1) }).strict(),
  z
    .object({
      newCustomer: z.object({
        firstName: z.string().trim().min(1).max(80),
        lastName: z.string().trim().max(80).optional(),
        phone: z.string().trim().min(4).max(32),
      }),
    })
    .strict(),
]);

export const linkAppointmentCustomerResponseSchema = z.object({
  appointmentId: z.string(),
  customerId: z.string(),
});

export const EditAppointmentPaymentSchema = z
  .object({
    appointmentId: z.string().min(1, "appointmentId is required"),
    price: z
      .number({ message: "price must be a number" })
      .min(0, "price must be at least 0")
      .optional(),
    tip: z
      .number({ message: "tip must be a number" })
      .min(0, "tip must be at least 0")
      .optional(),
    paymentType: checkoutPaymentTypeSchema.optional(),
    tipPaymentType: checkoutPaymentTypeSchema.optional(),
    note: z
      .string({ message: "note must be a string" })
      .max(1000, "note is too long")
      .optional(),
    reason: z
      .string({ message: "reason must be a string" })
      .max(500, "reason is too long")
      .optional(),
  })
  .refine(
    (data) =>
      data.price !== undefined ||
      data.tip !== undefined ||
      data.paymentType !== undefined ||
      data.tipPaymentType !== undefined ||
      data.note !== undefined,
    { message: "at least one field required" },
  );

export const RevertAppointmentSchema = z.object({
  appointmentId: z.string().min(1, "appointmentId is required"),
  reason: z
    .string({ message: "reason must be a string" })
    .max(500, "reason is too long")
    .optional(),
});

export const appointmentRefundPaymentSourceSchema = z.enum([
  "CHECKOUT",
  "DEPOSIT",
]);

export const RefundAppointmentSchema = z.object({
  appointmentId: z.string().min(1, "appointmentId is required"),
  /**
   * The appointment payment being refunded. Optional during the compatibility
   * window; callers should always send it so a charged deposit is not hidden
   * by a later checkout.
   */
  paymentSource: appointmentRefundPaymentSourceSchema.optional(),
  // Refund amount in minor units (cents). Omit to refund the full remaining
  // refundable amount.
  amountMinor: z
    .number({ message: "amountMinor must be a number" })
    .int("amountMinor must be an integer")
    .positive("amountMinor must be greater than 0")
    .optional(),
  reason: z
    .string({ message: "reason must be a string" })
    .trim()
    .min(3, "reason is required")
    .max(500, "reason is too long"),
  // Attribution recorded on the refund row (e.g. cancel-flow refunds).
  // Defaults to DASHBOARD when omitted.
  origin: z.enum(["DASHBOARD", "CANCELLATION"]).optional(),
});

export const FeedbackSchema = z.object({
  appointmentId: z.string().min(1, "appointmentId is required"),
  rating: z.number().min(1).max(5),
  comment: z.string().optional(),
});

export type ManualCheckoutInput = z.infer<typeof ManualCheckoutSchema>;
export type ServiceItemInput = z.infer<typeof ServiceItemSchema>;
export type CreateAppointmentInput = z.infer<typeof CreateAppointmentSchema>;
export type RescheduleAppointmentInput = z.infer<
  typeof RescheduleAppointmentSchema
>;
export type CancelAppointmentInput = z.infer<typeof CancelAppointmentSchema>;
export type AddNoteInput = z.infer<typeof AddNoteSchema>;
export type LinkAppointmentCustomerRequest = z.infer<
  typeof linkAppointmentCustomerRequestSchema
>;
export type LinkAppointmentCustomerResponse = z.infer<
  typeof linkAppointmentCustomerResponseSchema
>;
export type FeedbackInput = z.infer<typeof FeedbackSchema>;
export type EditAppointmentPaymentInput = z.infer<
  typeof EditAppointmentPaymentSchema
>;
export type RevertAppointmentInput = z.infer<typeof RevertAppointmentSchema>;
export type RefundAppointmentInput = z.infer<typeof RefundAppointmentSchema>;

export const appointmentIdResponseSchema = z.object({
  appointmentId: z.string().min(1),
});

export const bookingConversionSchema = z
  .object({
    eventId: z
      .string()
      .length(43)
      .regex(/^[A-Za-z0-9_-]+$/)
      .describe(
        "Opaque SHA-256 base64url conversion-deduplication ID; never an appointment or customer ID.",
      ),
    value: z
      .number()
      .finite()
      .nonnegative()
      .nullable()
      .describe(
        "Full booking value in major currency units. Null means a starting-at price is unresolved; an exact free booking is 0.",
      ),
    currency: z
      .literal("USD")
      .describe(
        "Canonical booking currency. Appointment payment rails and the platform ledger are currently USD-only.",
      ),
    managedGoogleAds: z
      .object({
        eventId: z
          .string()
          .length(43)
          .regex(/^[A-Za-z0-9_-]+$/)
          .describe(
            "Opaque persisted event ID used as the managed Google Ads transaction_id.",
          ),
        destination: z
          .string()
          .regex(/^AW-\d{1,20}\/[A-Za-z0-9_-]+$/)
          .describe(
            "Server-resolved managed Google Ads conversion destination.",
          ),
      })
      .strict()
      .optional(),
  })
  .strict()
  .describe(
    "Additive create-only conversion metadata for privacy-safe booking measurement.",
  );

export const createAppointmentResponseSchema = appointmentIdResponseSchema
  .extend({
    // Optional during rolling deploys so a newer web client can still accept
    // the legacy appointment-id-only response from an older API instance.
    conversion: bookingConversionSchema
      .optional()
      .describe(
        "Present on newly created appointments; optional so clients remain compatible during rolling API deploys.",
      ),
    googleAdsAutopilotAttributionDisposition:
      googleAdsAutopilotAttributionDispositionSchema
        .optional()
        .describe(
          "Server-only proxy signal: clear a consumed landing session, retain an ambiguous pending session, or treat it as unavailable.",
        ),
  })
  .strict();
export const rescheduleAppointmentResponseSchema = appointmentIdResponseSchema;
export const addNoteResponseSchema = appointmentIdResponseSchema;
export const editAppointmentPaymentResponseSchema = appointmentIdResponseSchema;
export const revertAppointmentResponseSchema = z.object({
  appointmentId: z.string().min(1),
  status: z.literal("SCHEDULED"),
});

export const appointmentRefundStatusSchema = z.enum([
  "PENDING",
  "PARTIAL",
  "REFUNDED",
  "DECLINED",
]);

export const refundAppointmentResponseSchema = z.object({
  appointmentId: z.string().min(1),
  refundId: z.string().min(1),
  paymentSource: appointmentRefundPaymentSourceSchema.optional(),
  stripeRefundId: z.string().nullable(),
  amountMinor: z.number().int(),
  status: appointmentRefundStatusSchema,
  remainingRefundableMinor: z.number().int(),
  reversalTransferId: z.string().nullable(),
});

/** Refundability for one independently refundable appointment payment. */
export const appointmentPaymentRefundabilitySchema = z.object({
  refundableMinor: z.number().int(),
  alreadyRefundedMinor: z.number().int(),
  originalMinor: z.number().int(),
  currency: z.string(),
  captureType: z.string().nullable(),
  isCash: z.boolean(),
  payoutReversalExpected: z.boolean(),
  /**
   * Staff cents a refund of all of `refundableMinor` would take back, from a
   * dry run of the ledger allocation. Scale it for a smaller amount. Absent
   * from older API payloads.
   */
  staffReversalMinor: z.number().int().optional(),
});

/**
 * Refundability snapshot used to drive the refund UI. The top-level values
 * describe the requested/default source. `sources` exposes checkout and
 * booking-deposit balances independently so either can be selected even when
 * both exist.
 */
export const appointmentRefundabilitySchema =
  appointmentPaymentRefundabilitySchema.extend({
    paymentSource: appointmentRefundPaymentSourceSchema.optional(),
    sources: z
      .object({
        CHECKOUT: appointmentPaymentRefundabilitySchema.nullable(),
        DEPOSIT: appointmentPaymentRefundabilitySchema.nullable(),
      })
      .optional(),
    /**
     * @deprecated Compatibility field for older API payloads. Prefer
     * `paymentSource`; treat a missing value as `CHECKOUT`.
     */
    kind: z.enum(["checkout", "deposit"]).optional(),
  });

export const sendAppointmentNotificationResponseSchema = z.object({
  appointmentId: z.string().min(1),
  sent: z.literal(true),
  channel: z.enum(["sms", "email"]),
  messages: z.number().int().min(1),
});
export type SendAppointmentNotificationResponse = z.infer<
  typeof sendAppointmentNotificationResponseSchema
>;

export const cancelAppointmentResponseSchema =
  appointmentIdResponseSchema.extend({
    alreadyCanceled: z.boolean().optional(),
    /**
     * What the verified cancel did with a charged deposit. Absent on the
     * already-canceled replay, which runs before any OTP and so must not
     * reveal a charged amount, and from older API payloads.
     */
    deposit: cancelledDepositOutcomeSchema.optional(),
  });

export const feedbackResponseSchema = appointmentIdResponseSchema.extend({
  alreadySubmitted: z.boolean().optional(),
});

export const successResponseSchema = z.object({ success: z.boolean() });

/**
 * An appointment's open card payment (#1053): the newest non-canceled attempt
 * with its live state, which a checkout without a card refuses beside.
 * `state`: `processing` (sent to a reader), `open` (on no reader: Tap to Pay,
 * or not sent yet), `failed`, `unknown` (its creation outcome is unknown),
 * `succeeded` (with the split a finalize records) or `sale`. Kept a string so
 * a state the API adds later still parses.
 */
export const openCardPaymentSchema = z.object({
  attemptId: z.string(),
  paymentIntentId: z.string().nullable(),
  readerId: z.string().nullable(),
  readerLabel: z.string().nullable(),
  state: z.string(),
  failureMessage: z.string().nullable(),
  amountMinor: z.number().nullable(),
  split: z
    .object({ serviceMinor: z.number(), tipMinor: z.number() })
    .nullable(),
});
export type OpenCardPayment = z.infer<typeof openCardPaymentSchema>;

/** `GET /v1/appointments/:id/card-payment`. */
export const appointmentCardPaymentResponseSchema = z.object({
  checkedOut: z.boolean(),
  payment: openCardPaymentSchema.nullable(),
});
export type AppointmentCardPaymentResponse = z.infer<
  typeof appointmentCardPaymentResponseSchema
>;

/** `POST /v1/appointments/:id/card-payment/cancel`. */
export const cancelAppointmentCardPaymentRequestSchema = z.object({
  attemptId: z.string().min(1),
});
export type CancelAppointmentCardPaymentRequest = z.infer<
  typeof cancelAppointmentCardPaymentRequestSchema
>;
export const cancelAppointmentCardPaymentResponseSchema = z.object({
  canceled: z.boolean(),
});
export type CancelAppointmentCardPaymentResponse = z.infer<
  typeof cancelAppointmentCardPaymentResponseSchema
>;

export const manualCheckoutResponseSchema = z.object({
  appointmentId: z.string().min(1),
  status: z.literal("COMPLETED"),
  price: z.number(),
  tip: z.number().nullable(),
  paymentType: checkoutPaymentTypeSchema,
  tipPaymentType: checkoutPaymentTypeSchema.nullable().optional(),
  note: z.string().nullable(),
  captureType: checkoutCaptureTypeSchema,
  stripeChargeId: z.string().nullable().optional(),
  stripeReceiptUrl: z.string().nullable().optional(),
  /** Present only when an offer was applied. `price` above is what was charged. */
  offer: z
    .object({
      offerId: z.string(),
      offerName: z.string(),
      grossPrice: z.number(),
      discountAmount: z.number(),
    })
    .nullable()
    .optional(),
});

export const appointmentHistoryServiceSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  duration: z.number().optional(),
  price: z.number().optional(),
  referenceId: z.string().optional(),
  optionId: z.string().optional(),
});

export const appointmentHistoryItemSchema = z.object({
  id: z.string().optional(),
  appointmentId: z.string(),
  date: z.string(),
  time: dbTimeSchema,
  datetime: z.string().optional(),
  formattedDatetime: z.string(),
  price: z.number().optional(),
  duration: z.number().optional(),
  isCanceled: z.boolean().optional(),
  isRescheduled: z.boolean().optional(),
  serviceTitle: z.string().optional(),
  locationName: z.string().optional(),
  memberName: z.string().optional(),
  services: z.array(appointmentHistoryServiceSchema).optional(),
});

export const appointmentHistoryResponseSchema = z.object({
  needCreditCard: z.boolean(),
  upcomingAppointments: z.array(appointmentHistoryItemSchema),
  hasUpcomingAppointments: z.boolean().optional(),
  customerId: z.string().optional(),
  verificationPurpose: z.string().optional(),
});

export const appointmentHistoryVerifiedRequestSchema = z
  .object({
    salonId: z.string().min(1),
    phoneNumber: z.string().min(5).optional(),
    email: z.string().email().optional(),
    code: verificationCodeSchema,
  })
  .superRefine((data, ctx) => {
    if (!data.phoneNumber && !data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phoneNumber"],
        message: "phoneNumber or email is required",
      });
    }
  });

export const appointmentHistoryParamsSchema = z.object({
  phoneNumber: z.string().min(5),
});

export const appointmentHistoryQuerySchema = z.object({
  salonId: z.string().optional(),
});

export const appointmentDetailServiceOptionSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  price: z.number().optional(),
  duration: z.number().optional(),
});

export const appointmentDetailServiceSchema = z.object({
  id: z.string(),
  title: z.string(),
  price: z.number(),
  duration: z.number(),
  description: z.string().optional(),
  /** Base price is "starting at" (render as `$60+`). Not applied to options. */
  up: z.boolean().optional(),
  option: appointmentDetailServiceOptionSchema.optional(),
});

/** @deprecated Schedule now carries address fields directly */
export const appointmentDetailScheduleLocationSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  parking: z.unknown().optional(),
  stripeTerminalLocationId: z.string().nullable().optional(),
});

export const appointmentDetailScheduleSchema = z.object({
  id: z.string(),
  title: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  stripeLocationId: z.string().nullable().optional(),
  /**
   * Stripe Terminal reader id (`tmr_…`) configured as the default POS device
   * for this Stripe Terminal location. Pre-selected in the POS reader picker
   * at checkout. UI hint only.
   */
  defaultStripeReaderId: z.string().nullable().optional(),
  parking: z.unknown().nullable().optional(),
  /** @deprecated Flattened — use schedule.stripeLocationId directly */
  location: appointmentDetailScheduleLocationSchema.nullable().optional(),
});

export const appointmentDetailSchema = z.object({
  id: z.string(),
  userId: z.string(),
  userName: z.string().nullable().optional(),
  teamMemberId: z.string(),
  teamMemberName: z.string().nullable().optional(),
  /**
   * The provider's `TeamMember.cashDeductionEnabled`. Authenticated read path
   * only — omitted from the public review payload. Retained for service
   * checkout and historical mixed-record compatibility.
   */
  teamMemberCashDeductionEnabled: z.boolean().optional(),
  salonId: z.string(),
  customerId: z.string().nullable().optional(),
  datetime: z.union([z.string(), z.date()]).optional(),
  startsAt: z.union([z.string(), z.date()]),
  endsAt: z.union([z.string(), z.date()]).nullable().optional(),
  status: z.string().optional(),
  notes: z.string().nullable().optional(),
  serviceTitle: z.string().nullable().optional(),
  price: z.number().nullable().optional(),
  durationMinutes: z.number().optional(),
  services: z.array(appointmentDetailServiceSchema),
  feedbackRating: z.number().nullable().optional(),
  feedbackComment: z.string().nullable().optional(),
  feedbackAt: z.union([z.string(), z.date()]).nullable().optional(),
  schedule: appointmentDetailScheduleSchema.nullable(),
  /**
   * Opaque metadata stored on creation. Only returned by authorized read
   * paths (owner/member-authenticated GET /v1/appointments/:id). Never
   * present on public review/history responses.
   */
  metadata: appointmentMetadataSchema.nullable().optional(),
});

/**
 * Public, unauthenticated post-booking review payload
 * (`GET /v1/appointments/:id/review`). Identical to {@link appointmentDetailSchema}
 * but with `metadata` and `teamMemberCashDeductionEnabled` omitted — both are
 * exposed ONLY on the authenticated owner/member read path. Kept as a derived
 * schema so the published contract matches runtime behavior exactly (no
 * advertised-but-never-returned field).
 */
export const appointmentReviewSchema = appointmentDetailSchema.omit({
  metadata: true,
  teamMemberCashDeductionEnabled: true,
});

export type AppointmentIdResponse = z.infer<typeof appointmentIdResponseSchema>;
export type CreateAppointmentResponse = z.infer<
  typeof createAppointmentResponseSchema
>;
export type RescheduleAppointmentResponse = z.infer<
  typeof rescheduleAppointmentResponseSchema
>;
export type AddNoteResponse = z.infer<typeof addNoteResponseSchema>;
export type CancelAppointmentResponse = z.infer<
  typeof cancelAppointmentResponseSchema
>;
export type FeedbackResponse = z.infer<typeof feedbackResponseSchema>;
export type ManualCheckoutResponse = z.infer<
  typeof manualCheckoutResponseSchema
>;
export type EditAppointmentPaymentResponse = z.infer<
  typeof editAppointmentPaymentResponseSchema
>;
export type RevertAppointmentResponse = z.infer<
  typeof revertAppointmentResponseSchema
>;
export type AppointmentRefundStatus = z.infer<
  typeof appointmentRefundStatusSchema
>;
export type AppointmentRefundPaymentSource = z.infer<
  typeof appointmentRefundPaymentSourceSchema
>;
export type RefundAppointmentResponse = z.infer<
  typeof refundAppointmentResponseSchema
>;
export type AppointmentPaymentRefundability = z.infer<
  typeof appointmentPaymentRefundabilitySchema
>;
export type AppointmentRefundability = z.infer<
  typeof appointmentRefundabilitySchema
>;
export type SuccessResponse = z.infer<typeof successResponseSchema>;
export type AppointmentHistoryService = z.infer<
  typeof appointmentHistoryServiceSchema
>;
export type AppointmentHistoryItem = z.infer<
  typeof appointmentHistoryItemSchema
>;
export type AppointmentHistoryResponse = z.infer<
  typeof appointmentHistoryResponseSchema
>;
export type AppointmentHistoryVerifiedRequest = z.infer<
  typeof appointmentHistoryVerifiedRequestSchema
>;
export type AppointmentHistoryParams = z.infer<
  typeof appointmentHistoryParamsSchema
>;
export type AppointmentHistoryQuery = z.infer<
  typeof appointmentHistoryQuerySchema
>;

export type AppointmentDetailServiceOption = z.infer<
  typeof appointmentDetailServiceOptionSchema
>;
export type AppointmentDetailService = z.infer<
  typeof appointmentDetailServiceSchema
>;
export type AppointmentDetailScheduleLocation = z.infer<
  typeof appointmentDetailScheduleLocationSchema
>;
export type AppointmentDetailSchedule = z.infer<
  typeof appointmentDetailScheduleSchema
>;
export type AppointmentDetail = z.infer<typeof appointmentDetailSchema>;

export type AppointmentCancellationReason = z.infer<
  typeof appointmentCancellationReasonSchema
>;
export type AppointmentOperatorStatusRequest = z.infer<
  typeof appointmentOperatorStatusRequestSchema
>;
export type AppointmentOperatorStatusResponse = z.infer<
  typeof appointmentOperatorStatusResponseSchema
>;
export type AppointmentOperatorRescheduleRequest = z.infer<
  typeof appointmentOperatorRescheduleRequestSchema
>;
export type AppointmentOperatorRescheduleResponse = z.infer<
  typeof appointmentOperatorRescheduleResponseSchema
>;
